import type { MouseEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getEnabledNav } from '@/shared/config/modules';
import type { ModuleNavEntry } from '@/shared/config/modules';

/**
 * AppNav — renders navigation entries for all enabled modules.
 *
 * Reads the enabled-module nav entries from the module registry and renders
 * them as a vertical list. Disabled modules are simply absent from the list.
 *
 * Navigation is SPA-internal: each item keeps a real `href` (for accessibility
 * and open-in-new-tab), but a normal left-click is intercepted and routed via
 * TanStack Router's `navigate` so the in-memory session is preserved — a full
 * page reload would drop the in-memory access token. Labels are localized via
 * react-i18next (the registry stores i18n keys, not display text).
 */

function NavItem({ entry }: { entry: ModuleNavEntry }) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    // Let modified clicks (new tab/window) and non-primary buttons behave natively.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    void navigate({ to: entry.path as string });
  }

  return (
    <li>
      <a
        href={entry.path}
        onClick={handleClick}
        className="flex min-h-[44px] items-center rounded px-3 py-3 text-sm text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground"
        aria-label={t(entry.labelKey)}
      >
        {entry.icon && (
          <span data-icon={entry.icon} aria-hidden="true" className="mr-2 inline-block" />
        )}
        <span>{t(entry.labelKey)}</span>
      </a>
    </li>
  );
}

export function AppNav() {
  const { t } = useTranslation('common');
  const navEntries = getEnabledNav();

  if (navEntries.length === 0) {
    return null;
  }

  return (
    <nav aria-label={t('shell.nav.mainLabel')}>
      <ul role="list" className="m-0 list-none p-0">
        {navEntries.map((entry) => (
          <NavItem key={entry.path} entry={entry} />
        ))}
      </ul>
    </nav>
  );
}
